// QuizManagement.jsx — Admin quiz list with questions modal
import React, { useState, useEffect } from 'react';
import { Trash2, List, Clock, FileText, X, CheckCircle, AlertCircle } from 'lucide-react';
import * as d1Api from '../../services/d1Api';

const optionLabels = { a: 'أ', b: 'ب', c: 'ج', d: 'د' };

// ── Questions Modal ──
const QuestionsModal = ({ quiz, onClose }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!quiz?.id) return;
    setLoading(true);
    d1Api.getQuizQuestions(quiz.id).then((res) => {
      if (res.success) setQuestions(res.data || []);
      setLoading(false);
    });
  }, [quiz?.id]);

  const handleDeleteQuestion = async (qId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا السؤال؟')) return;
    const res = await d1Api.deleteQuizQuestion(qId);
    if (res.success) {
      setQuestions((prev) => prev.filter((q) => q.id !== qId));
    } else {
      alert('❌ فشل الحذف: ' + (res.error || 'غير معروف'));
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-12 overflow-y-auto" onClick={onClose}>
      <div className="glass-panel w-full max-w-3xl mx-4 mb-12 p-6" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
          <div>
            <h3 className="text-xl font-bold text-white">أسئلة: {quiz.title}</h3>
            <p className="text-sm text-slate-400 mt-1">{loading ? '...' : `${questions.length} سؤال`}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition">
            <X size={20} className="text-slate-400" />
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="py-16 text-center">
            <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-400">جاري تحميل الأسئلة...</p>
          </div>
        ) : questions.length === 0 ? (
          <div className="py-16 text-center">
            <AlertCircle size={48} className="mx-auto mb-4 opacity-30" />
            <p className="text-slate-400 text-lg">لا توجد أسئلة في هذا الامتحان</p>
          </div>
        ) : (
          <div className="space-y-4" dir="rtl">
            {questions.map((q, idx) => (
              <div key={q.id} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                {/* Question text */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-start gap-3 flex-1">
                    <span className="w-8 h-8 rounded-lg bg-purple-500/20 text-purple-400 flex items-center justify-center text-sm font-bold shrink-0">
                      {idx + 1}
                    </span>
                    <p className="font-bold text-white leading-relaxed">{q.question_text}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteQuestion(q.id)}
                    className="p-1.5 hover:bg-red-500/20 rounded-lg transition shrink-0"
                    title="حذف السؤال"
                  >
                    <Trash2 size={16} className="text-red-400" />
                  </button>
                </div>

                {/* Options */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {['a', 'b', 'c', 'd'].map((key) => {
                    const text = q[`option_${key}`];
                    if (!text) return null;
                    const isCorrect = q.correct_answer === key;
                    return (
                      <div
                        key={key}
                        className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold ${
                          isCorrect
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                            : 'bg-white/5 text-slate-300 border border-white/5'
                        }`}
                      >
                        {isCorrect && <CheckCircle size={14} className="shrink-0" />}
                        <span className="w-6 h-6 rounded bg-white/10 flex items-center justify-center text-xs shrink-0">
                          {optionLabels[key]}
                        </span>
                        <span className="flex-1">{text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Component ──
export const QuizManagement = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuiz, setSelectedQuiz] = useState(null);

  useEffect(() => {
    loadQuizzes();
  }, []);

  const loadQuizzes = async () => {
    setLoading(true);
    const res = await d1Api.getAllQuizzes();
    if (res.success) setQuizzes(res.data || []);
    setLoading(false);
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`هل أنت متأكد من حذف الامتحان "${title}"؟ لا يمكن التراجع عن هذا الإجراء.`)) return;
    const res = await d1Api.deleteQuiz(id);
    if (res.success) {
      setQuizzes((prev) => prev.filter((q) => q.id !== id));
    } else {
      alert('❌ حدث خطأ أثناء الحذف: ' + (res.error || 'غير معروف'));
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <FileText size={24} />
        📝 الامتحانات
      </h2>

      {loading ? (
        <div className="glass-panel p-12 text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">جاري التحميل...</p>
        </div>
      ) : quizzes.length === 0 ? (
        <div className="glass-panel p-12 text-center">
          <FileText size={48} className="mx-auto mb-4 opacity-30" />
          <h3 className="text-xl font-bold mb-2">لا يوجد امتحانات حالياً</h3>
          <p className="text-slate-400">أنشئ امتحان جديد من صفحة الكورسات</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quizzes.map((q) => (
            <div key={q.id} className="glass-panel p-5 flex flex-col justify-between">
              <div>
                <h3 className="font-bold text-lg mb-2">{q.title}</h3>
                <div className="flex items-center gap-2 text-sm text-slate-400 mb-1">
                  <Clock size={14} />
                  <span>المدة: {q.duration_minutes || 0} دقيقة</span>
                </div>
                {q.course_id && (
                  <p className="text-xs text-slate-500 truncate">كورس: {q.course_id}</p>
                )}
              </div>

              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/10">
                <button
                  onClick={() => setSelectedQuiz(q)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500 hover:text-white rounded-xl font-bold text-sm transition"
                >
                  <List size={16} />
                  الأسئلة
                </button>
                <button
                  onClick={() => handleDelete(q.id, q.title)}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-white rounded-xl font-bold text-sm transition"
                >
                  <Trash2 size={16} />
                  حذف
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Questions Modal */}
      {selectedQuiz && (
        <QuestionsModal quiz={selectedQuiz} onClose={() => setSelectedQuiz(null)} />
      )}
    </div>
  );
};